import {Col, Form, InputNumber, Radio, Row, Select} from 'antd';
import React from 'react';

import {CommonErrors} from '../../../constants/Common';
import {LCI_FIELD_HINTS} from '../constants/lciFieldHints';
import {
  PCF_COL_GUTTER_CLASS,
  PCF_COL_SPAN_HALF,
  rowGutter
} from '../constants/pcfFormLayout';
import {
  DISPOSAL_ROUTE_OPTIONS,
  DISPOSAL_TRANSPORT_OPTIONS,
  RECOVERABLE_VALUE_OPTIONS
} from '../constants/pcfOptions';
import {CEndOfLifeKeys, CEndOfLifeLabels} from '../PCFKeyLabels';

import {FormItemLabel} from './FormItemLabel';
import {positiveUnitsRule} from './lciValidation';
import PCFAddButton from './PCFAddButton';
import RemovableCard from './RemovableCard';

const BASE = 'c';

const blankRoute = () => ({
  route: 'landfill',
  percent: ''
});

export const cEndOfLifeDefaults = {
  disposalRoutes: [blankRoute()],
  disposalTransport: '',
  hasRecoverableValue: 'no',
  recoverableType: '',
  recoverableFraction: '',
  items: [] as Array<{
    subStage: string;
    activity: string;
    qty: number | string;
    unit: string;
  }>
};

function DisposalRouteFields({name}: {name: number}) {
  return (
    <Row gutter={rowGutter}>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[name, CEndOfLifeKeys.DISPOSAL_ROUTE]}
          label={CEndOfLifeLabels.DISPOSAL_ROUTE}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(CEndOfLifeLabels.DISPOSAL_ROUTE)
            }
          ]}
        >
          <Select className="select" options={DISPOSAL_ROUTE_OPTIONS} />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[name, CEndOfLifeKeys.DISPOSAL_PERCENT]}
          required
          label={
            <FormItemLabel hint={LCI_FIELD_HINTS.disposalRoutes}>
              {CEndOfLifeLabels.DISPOSAL_PERCENT}
            </FormItemLabel>
          }
          rules={[positiveUnitsRule(CEndOfLifeLabels.DISPOSAL_PERCENT)]}
        >
          <InputNumber
            controls={false}
            className="input"
            style={{width: '100%'}}
            min={0}
            max={100}
          />
        </Form.Item>
      </Col>
    </Row>
  );
}

export default function CEndOfLifeSection(): JSX.Element {
  const form = Form.useFormInstance();
  const hasRecoverableValue = Form.useWatch(
    [BASE, CEndOfLifeKeys.HAS_RECOVERABLE_VALUE],
    form
  );

  return (
    <div className="lci-section">
      <div className="lci-section-header">
        <h3 className="lci-section-title">C · End of Life (C1-C4)</h3>
      </div>

      <Form.List
        name={[BASE, CEndOfLifeKeys.DISPOSAL_ROUTES]}
        rules={[
          {
            validator: async (_, routes) => {
              if (!routes?.length) {
                await Promise.resolve();

                return;
              }

              const total = routes.reduce(
                (sum: number, row: {percent?: string | number}) => {
                  const raw = row.percent;
                  const parsed =
                    raw == null || raw === '' ? null : parseFloat(String(raw));

                  return (
                    sum +
                    (parsed != null && Number.isFinite(parsed) ? parsed : 0)
                  );
                },
                0
              );

              if (Math.abs(total - 100) > 0.01) {
                return await Promise.reject(
                  new Error('End-of-life route percentages must sum to 100%.')
                );
              }

              await Promise.resolve();
            }
          }
        ]}
      >
        {(fields, {add, remove}, {errors: listErrors}) => (
          <>
            {listErrors?.length > 0 && (
              <div className="field-error">{listErrors[0]}</div>
            )}
            <Form.Item
              label={
                <FormItemLabel hint={LCI_FIELD_HINTS.disposalRoutes}>
                  {CEndOfLifeLabels.DISPOSAL_ROUTES}
                </FormItemLabel>
              }
            >
              <PCFAddButton
                onClick={() => {
                  add(blankRoute());
                }}
              >
                Add Disposal Route
              </PCFAddButton>
            </Form.Item>
            <div className="lci-cards-list">
              {fields.map(({key, name}, idx) => (
                <RemovableCard
                  key={key}
                  title={`Route #${idx + 1}`}
                  onRemove={() => {
                    remove(name);
                  }}
                  showRemove={fields.length > 1}
                >
                  <DisposalRouteFields name={name} />
                </RemovableCard>
              ))}
            </div>
          </>
        )}
      </Form.List>

      <Row gutter={rowGutter}>
        <Col className={PCF_COL_GUTTER_CLASS} span={24}>
          <Form.Item
            name={[BASE, CEndOfLifeKeys.DISPOSAL_TRANSPORT]}
            label={CEndOfLifeLabels.DISPOSAL_TRANSPORT}
            rules={[
              {
                required: true,
                message: CommonErrors.mandatory(
                  CEndOfLifeLabels.DISPOSAL_TRANSPORT
                )
              }
            ]}
          >
            <Radio.Group options={DISPOSAL_TRANSPORT_OPTIONS} />
          </Form.Item>
        </Col>
        <Col className={PCF_COL_GUTTER_CLASS} span={24}>
          <Form.Item
            name={[BASE, CEndOfLifeKeys.HAS_RECOVERABLE_VALUE]}
            label={CEndOfLifeLabels.HAS_RECOVERABLE_VALUE}
            rules={[{required: true, message: 'Required'}]}
          >
            <Radio.Group options={RECOVERABLE_VALUE_OPTIONS} />
          </Form.Item>
        </Col>
        {(hasRecoverableValue === 'recyclable_content' ||
          hasRecoverableValue === 'energy_recovery') && (
          <>
            <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
              <Form.Item
                name={[BASE, CEndOfLifeKeys.RECOVERABLE_TYPE]}
                label={CEndOfLifeLabels.RECOVERABLE_TYPE}
                rules={[
                  {
                    required: true,
                    message: CommonErrors.mandatory(
                      CEndOfLifeLabels.RECOVERABLE_TYPE
                    )
                  }
                ]}
              >
                <Select
                  className="select"
                  placeholder="Material type"
                  options={DISPOSAL_ROUTE_OPTIONS}
                />
              </Form.Item>
            </Col>
            <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
              <Form.Item
                name={[BASE, CEndOfLifeKeys.RECOVERABLE_FRACTION]}
                required={
                  hasRecoverableValue === 'recyclable_content' ||
                  hasRecoverableValue === 'energy_recovery'
                }
                label={CEndOfLifeLabels.RECOVERABLE_FRACTION}
                rules={[
                  positiveUnitsRule(CEndOfLifeLabels.RECOVERABLE_FRACTION)
                ]}
              >
                <InputNumber
                  controls={false}
                  className="input"
                  style={{width: '100%'}}
                  min={0}
                  max={100}
                />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>
    </div>
  );
}
